'use client';

import { useEffect, useRef, useState } from 'react';
import { css, cx } from '@bts-web/utils-style-engine';
import type { TransactionsViewQuery } from '@bts-web/data-layer/server';
import { getTransactions } from '../gqlActions/getTransactions.action';
import { TransactionsWidgetSkeleton } from './TransactionsWidgetSkeleton';
import { WidgetTranslationsKeys } from './utils/getTransactionsWidgetTranslations';
import { itemsListHeaderStyles } from '../styles';
import { ContentSectionWrapper } from '../../layouts/ContentSectionWrapper/ContentSectionWrapper';
import { ButtonBase, Link } from '../components';
import { TransactionList } from '../../transactions/TransactionList/TransactionList';
import { TransactionItemTranslationsKeys } from '../../transactions/types/transactions';
import { FilledArrowRight } from '../svgIcons';
import { useHandleGqlErrorsWithSnackbar } from '../utils/errors';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { datadogErrorHelper } from '../utils/datadogErrorHelper';

type Transactions = NonNullable<
  NonNullable<TransactionsViewQuery['response']['transactions']>['edges']
>;

export interface BaseTransactionsWidgetProps {
  fetchNumberOfItems: number;
  widgetTranslations: WidgetTranslationsKeys;
  translations: TransactionItemTranslationsKeys;
  locale: string;
  anchorId?: string;
}

export interface PortfolioTransactionsProps
  extends BaseTransactionsWidgetProps {
  variant: 'Portfolio';
  assetId?: never;
}

export interface AssetDetailsTransactionsProps
  extends BaseTransactionsWidgetProps {
  variant: 'AssetDetails';
  assetId: string;
}

export type TransactionsWidgetProps =
  | PortfolioTransactionsProps
  | AssetDetailsTransactionsProps;

// mirrors anchorScrollMarginClass in asset-details/AnchorNavigation config
// (kept local to avoid a common -> asset-details dependency)
const anchorScrollMarginStyles = css({
  scrollMarginTop: { base: '112px', lg: '128px' },
});

const transactionsLinkStyles = css({
  gap: 'extra_small_2',
  fontSize: 'small',
  display: 'flex',
  alignItems: 'center',
});

const descriptionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
  color: 'neutrals.text_primary',
});

const wrapperStyles = css({ backgroundColor: 'neutrals.card_fill_primary' });

const transactionListStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small',
});

const transactionLinkWrapperStyles = css({ mt: 'extra_small' });

export const LazyLoadedTransactionsWidget = ({
  assetId,
  fetchNumberOfItems,
  variant,
  widgetTranslations,
  translations,
  locale,
  anchorId,
}: TransactionsWidgetProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [transactions, setTransactions] = useState<Transactions | null>(null);

  const [hidden, setHidden] = useState(false);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const triggerTransactionsFetch = async () => {
    try {
      const response = await getTransactions(
        {
          input: {
            ...(assetId && { assetId }),
            first: fetchNumberOfItems,
          },
        },
        false,
        true,
      );

      if (response.errors) {
        setHidden(true);

        processErrors(response.errors);

        return;
      }

      const edges =
        (
          response?.data?.transactions as NonNullable<
            TransactionsViewQuery['response']['transactions']
          >
        )?.edges ?? [];

      setTransactions(edges);
    } catch (error) {
      setHidden(true);

      datadogErrorHelper({
        errorMessage: 'Error fetching transactions: ' + JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.HIGH,
      });
    }
  };

  useEffect(() => {
    if (variant === 'AssetDetails') {
      triggerTransactionsFetch();

      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;

      observer.disconnect();

      triggerTransactionsFetch();
    });

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [assetId, fetchNumberOfItems, variant]);

  if (hidden || (variant === 'AssetDetails' && !transactions?.length)) {
    return null;
  }

  const transactionsLink = (
    <Link
      size="small"
      prefetch={false}
      href={assetId ? `/transactions?assetId=${assetId}` : '/transactions'}
    >
      <ButtonBase
        size="extra_small"
        visual="tertiary"
        withBorderRadius={false}
        className={css({ borderRadius: '32px' })}
      >
        <span className={transactionsLinkStyles}>
          {assetId
            ? widgetTranslations.viewRelatedTransactions
            : widgetTranslations.viewAllTransactions}

          <FilledArrowRight size={16} data-dir="rtl" />
        </span>
      </ButtonBase>
    </Link>
  );

  if (variant === 'AssetDetails') {
    return <ContentSectionWrapper>{transactionsLink}</ContentSectionWrapper>;
  }

  const noTransactionsToShow =
    transactions !== null && transactions.length === 0;

  return (
    <ContentSectionWrapper
      sectionTitle={widgetTranslations.latestTransactions}
      {...(noTransactionsToShow
        ? { id: 'latest-transactions' }
        : anchorId
          ? { id: anchorId }
          : {})}
      data-testid="transactions widget"
      className={cx(
        wrapperStyles,
        anchorId ? anchorScrollMarginStyles : undefined,
      )}
    >
      <div ref={sentinelRef} />
      {transactions === null ? (
        <TransactionsWidgetSkeleton />
      ) : noTransactionsToShow ? (
        <div className={descriptionStyles}>
          <p data-testid="empty-transactions">
            {widgetTranslations.emptyStateNoTransactions}
          </p>

          <p data-testid="latest-transactions-description">
            {widgetTranslations.latestTransactionsDescription}
          </p>
        </div>
      ) : (
        <div className={transactionListStyles}>
          <div className={itemsListHeaderStyles}>
            <span>{widgetTranslations.transactionDescription}</span>
            <span>{widgetTranslations.amount}</span>
          </div>
          <TransactionList
            transactions={transactions}
            translations={translations}
            locale={locale}
          />
          <div className={transactionLinkWrapperStyles}>{transactionsLink}</div>
        </div>
      )}
    </ContentSectionWrapper>
  );
};
